<template>
  <div>
    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="isOpen"
          class="side-sheet-overlay"
          @click.self="handleClose"
        />
      </Transition>

      <Transition name="slide">
        <div
          v-if="isOpen"
          class="side-sheet-panel"
        >
          <div class="side-sheet-header">
            <h2 class="side-sheet-title">{{ title }}</h2>
            <button
              class="side-sheet-close"
              @click="handleClose"
              aria-label="Close"
            >
              <ui-icon> close </ui-icon>
            </button>
          </div>

          <div class="side-sheet-content">
            <slot></slot>
          </div>

          <div class="side-sheet-actions">
            <AcvButton
              data-test-id="side-sheet__button"
              :disabled="isDisabled"
              :variant="actionVariant"
              @click="emit('onActionClick')"
            >
              {{ actionText }}
            </AcvButton>
            <AcvButton
              v-if="showCancelAction"
              data-test-id="side-sheet__cancel-button"
              variant="link"
              @click="handleClose"
              class="cancel-button"
            >
              {{ cancelText }}
            </AcvButton>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { watch } from 'vue';
import { useOverlayScrollHelper } from '@/composables/useOverlayScrollHelper';

const emit = defineEmits(['onActionClick', 'toggleSideSheet']);

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: true,
  },
  title: {
    type: String,
    default: '',
  },
  actionText: {
    type: String,
    default: '',
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  showCancelAction: {
    type: Boolean,
    default: false,
  },
  cancelText: {
    type: String,
    default: 'Cancel',
  },
  actionVariant: {
    type: String,
    default: 'primary',
  },
});

const { preventBgFromScrolling, allowBgToScroll } = useOverlayScrollHelper();

const handleClose = () => {
  emit('toggleSideSheet', false);
};

watch(
  () => props.isOpen,
  (isSideSheetOpen) => {
    if (isSideSheetOpen) {
      preventBgFromScrolling();
    } else {
      allowBgToScroll();
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.side-sheet-overlay {
  @apply tw-fixed tw-inset-0 tw-z-50 tw-bg-black/50;
}

.side-sheet-panel {
  @apply tw-fixed tw-bottom-0 tw-right-0 tw-z-50 tw-bg-white tw-shadow-2xl;
  @apply tw-flex tw-flex-col;
  @apply tw-w-full tw-max-h-svh;

  @screen tw-md {
    @apply tw-top-0 tw-h-svh tw-w-[375px] tw-rounded-l-2xl;
  }
}

.side-sheet-header {
  @apply tw-flex tw-items-center tw-justify-between tw-pl-4 tw-pr-0 tw-pt-2;
  @apply tw-flex-shrink-0;
}

.side-sheet-title {
  @apply tw-m-0 tw-text-xl tw-font-medium tw-leading-[30px] tw-tracking-[0.15px];
}

.side-sheet-close {
  @apply tw-flex tw-size-12 tw-items-center tw-justify-center;
  @apply tw-rounded-full;
  @apply tw-cursor-pointer tw-transition-colors;
  @apply tw-border-0 tw-bg-transparent;
}

.side-sheet-content {
  @apply tw-min-h-0 tw-px-4;
  @apply tw-overflow-auto;

  @screen tw-md {
    @apply tw-flex-1;
  }
}

.side-sheet-actions {
  @apply tw-flex tw-items-center tw-p-4;
  @apply tw-flex-shrink-0 tw-bg-white;
}

.cancel-button {
  @apply tw-ml-4;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from {
  @apply tw-translate-y-full;

  @screen tw-md {
    @apply tw-translate-x-full tw-translate-y-0;
  }
}

.slide-leave-to {
  @apply tw-translate-y-full;

  @screen tw-md {
    @apply tw-translate-x-full tw-translate-y-0;
  }
}
</style>
